// SQL Autocomplete Test Utilities
// This file provides utilities to test and debug the SQL autocomplete functionality

import { DatabaseOperationsService } from '@/services/api/services/DatabaseOperationsService';

// Extend window type for Monaco editor testing
declare global {
  interface Window {
    monacoEditor?: {
      getValue(): string;
      getPosition(): { lineNumber: number; column: number };
      setValue(value: string): void;
      setPosition(position: { lineNumber: number; column: number }): void;
      trigger(source: string, action: string, payload?: unknown): void;
      getModel(): unknown;
    };
    monaco?: {
      languages: {
        getLanguages(): unknown[];
        getCompletionProviders(languageId: string): unknown;
      };
    };
    createAutocompleteTest?: typeof createAutocompleteTest;
    testSqlAutocomplete?: ReturnType<typeof createAutocompleteTest>;
  }
}

export function createAutocompleteTest() {
  // Test SQL queries for different scenarios
  const testQueries = {
    basicSelect: 'SELECT * FROM users WHERE ',
    joinQuery: 'SELECT u.name, p.title FROM users u JOIN posts p ON u.id = p.user_id WHERE u.',
    dotNotation: 'SELECT users.',
    afterTable: 'SELECT * FROM users ',
    multipleJoins: 'SELECT * FROM users u JOIN posts p ON u.id = p.user_id JOIN comments c ON p.id = c.post_id WHERE c.'
  };

  // Function to test column fetching for a specific table
  async function testColumnFetch(datasourceId: string, databaseName: string, tableName: string, schemaName?: string, databaseType?: string) {
    try {
      console.log(`Testing column fetch for ${tableName}...`);
      
      const response = await DatabaseOperationsService.agentProxyCommand({
        datasourceId,
        requestBody: {
          type: 'get_column_info',
          timeout: 30,
          data: {
            database_name: databaseName,
            table_name: tableName,
            schema_name: databaseType === 'PostgreSQL' && schemaName ? schemaName : undefined,
            nonce: crypto.randomUUID(),
            timestamp: Math.floor(Date.now() / 1000)
          }
        }
      });

      console.log(`Columns for ${tableName}:`, response);
      // Type guard to check if response has columns property
      if ('columns' in response && Array.isArray(response.columns)) {
        return response.columns;
      }
      return [];
    } catch (error) {
      console.error(`Error fetching columns for ${tableName}:`, error);
      throw error;
    }
  }

  // Function to test SQL parsing and table extraction
  function testSqlParsing(sql: string) {
    console.log(`Testing SQL parsing for: ${sql}`);
    
    // Table extraction patterns
    const patterns = [
      /FROM\s+([a-zA-Z_][a-zA-Z0-9_]*)/gi,
      /JOIN\s+([a-zA-Z_][a-zA-Z0-9_]*)/gi,
      /UPDATE\s+([a-zA-Z_][a-zA-Z0-9_]*)/gi,
      /INSERT\s+INTO\s+([a-zA-Z_][a-zA-Z0-9_]*)/gi
    ];
    
    const extractedTables: string[] = [];
    for (const pattern of patterns) {
      let match;
      while ((match = pattern.exec(sql)) !== null) {
        const [, tableName] = match;
        if (!extractedTables.includes(tableName)) {
          extractedTables.push(tableName);
        }
      }
    }

    console.log('Extracted tables:', extractedTables);
    return extractedTables;
  }

  // Function to test Monaco editor autocomplete
  function testMonacoAutocomplete() {
    const editor = window.monacoEditor;
    if (!editor) {
      console.error('Monaco editor not found. Make sure you have opened the SQL editor.');
      return;
    }

    console.log('Monaco Editor Test:');
    console.log('Current value:', editor.getValue());
    console.log('Current position:', editor.getPosition());

    // Test setting a query
    const query = 'SELECT * FROM users WHERE ';
    // Note: In production, this should use a proper confirmation dialog
    // For now, we'll just log and proceed without confirmation for testing
    console.log('Setting test query in editor');
    editor.setValue(query);
    editor.setPosition({ lineNumber: 1, column: query.length + 1 });

    // Try to trigger autocomplete
    setTimeout(() => {
      editor.trigger('keyboard', 'editor.action.triggerSuggest', {});
    }, 500);
  }
  
  // Function to inspect autocomplete provider state
  function inspectAutocompleteProvider() {
    const editor = window.monacoEditor;
    if (!editor) {
      console.error('Monaco editor not found.');
      return;
    }

    // Get the model and check for registered providers
    const model = editor.getModel();
    if (!model) {
      console.error('No model found.');
      return;
    }

    if (typeof model === 'object' && model !== null && 'getLanguageId' in model) {
      const modelObj = model as { getLanguageId?: unknown }
      if (typeof modelObj.getLanguageId === 'function') {
        const getLanguageId = modelObj.getLanguageId as () => string
        console.log('Model language:', getLanguageId());
      } else {
        console.log('Model language: unknown');
      }
    } else {
      console.log('Model language: unknown');
    }
    
    // Check monaco instance
    const { monaco } = window;
    if (monaco) {
      const languages = monaco.languages.getLanguages();
      console.log('Registered languages:', Array.isArray(languages)
        ? languages.map((l: unknown) => {
            if (l && typeof l === 'object' && 'id' in l) {
              const langObj = l as { id?: unknown }
              return typeof langObj.id === 'string' ? langObj.id : ''
            }
            return ''
          }).filter(Boolean)
        : 'none');
      
      // Try to get completion providers
      console.log('Monaco languages service exists:', !!monaco.languages);
    }
  }

  return {
    testQueries,
    testColumnFetch,
    testSqlParsing,
    testMonacoAutocomplete,
    inspectAutocompleteProvider
  };
}

// Make it available globally for browser console testing
if (typeof window !== 'undefined') {
  window.createAutocompleteTest = createAutocompleteTest;

  // Auto-create test utilities
  window.testSqlAutocomplete = createAutocompleteTest();
}
